import { useCallback, useState, useEffect, useRef } from 'react'

import { useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { captureException } from '@sentry/react'

import { paymentCompletedEvent, purchaseEvent } from 'src/Helpers/TagManager'
import {
  useGetPaymentGtmQuery,
  usePaymentEventCompletedMutation,
} from 'src/Redux/analyticsApi'
import { CampaignPlatformTypeEnum } from 'src/Types/campaigns'
import {
  getIsFirstCampaign,
  getUserPhone,
  getUserSelector,
} from 'src/Redux/auth-process/userSlice/selectors'
import { getPaymentMethod } from 'src/Redux/payment-process/selectors'
import { PaymentMethodEnum } from 'src/Constants/enums'
import { useGetSubscriptionQuery } from 'src/Redux/subscription-process/subscriptionApi'

import { useCustomSearchParams } from './useCustomSearchParams'

interface PaymentCompletedEvents {
  handlePaymentCompletedEvents: (params: { platformName: string }) => void
}

export const usePaymentCompletedEvents = (): PaymentCompletedEvents => {
  const param = useParams()
  const [platformName, setPlatformName] = useState('')
  const hasFiredRef = useRef(false)
  const { data: plan } = useGetSubscriptionQuery()
  const [paymentEventCompleted] = usePaymentEventCompletedMutation()
  const campaignId = Number(param.number) || 0
  const { data: gtmData } = useGetPaymentGtmQuery(
    {
      campaignId,
      platform: platformName,
    },
    { skip: !platformName },
  )
  const isFirstCampaign = useSelector(getIsFirstCampaign)
  const user = useSelector(getUserSelector)
  const phone = useSelector(getUserPhone)
  const paymentMethod = useSelector(getPaymentMethod)
  const { search } = useCustomSearchParams()
  const paypalBillingAgreement = search.get('autopay')

  const handlePaymentCompletedEvents = useCallback(
    (params: { platformName: string }): void => {
      if (params.platformName === CampaignPlatformTypeEnum.META) {
        return
      }
      setPlatformName(params.platformName)
    },
    [],
  )

  // Process payment events when gtmData is loaded
  useEffect(() => {
    if (!gtmData || !platformName || hasFiredRef.current) {
      return
    }
    hasFiredRef.current = true

    const processPaymentEvents = async (): Promise<void> => {
      try {
        const payload = gtmData
        // if user is not logged in, we can't get user id
        // so we need to capture this error
        // and send it to sentry
        if (!user.id) {
          captureException(new Error(JSON.stringify(user)), {
            tags: {
              section: 'usePaymentCompletedEvents-userid',
            },
          })
        }
        paymentCompletedEvent({
          campaignId,
          platformName,
          paymentMethod:
            paypalBillingAgreement === 'true'
              ? PaymentMethodEnum.PAYPAL_BILLING_AGREEMENT
              : paymentMethod,
          genresList: payload.genresList.join(', ') || '',
          userId: user.id,
          userRole: user.role,
          email: user.email,
          phone: phone || '',
          campaignNumber: Number(payload.campaignNumber),
          campaignType: payload.campaignType,
          campaignValue: payload.resultPrice,
          paymentAmount: payload.value,
          transactionId: payload.transactionId,
          isAnyLanguage:
            Boolean(payload.languagesList.join(', ').includes('Any')) || false,
          subscriptionPlan: plan?.package?.name || '',
        })

        purchaseEvent({
          user,
          isFirstCampaign,
          campaignId,
          transactionTotal: payload.value,
          price: payload.resultPrice,
          campaignPlatform: platformName,
        })

        void paymentEventCompleted({
          campaignId,
          campaignPlatform: platformName as CampaignPlatformTypeEnum,
        })
      } catch (err) {
        captureException(err, {
          tags: {
            section: 'usePaymentCompletedEvents-handlePaymentCompleted',
          },
        })
      }
    }

    void processPaymentEvents()
  }, [
    gtmData,
    platformName,
    campaignId,
    user,
    phone,
    paymentMethod,
    paypalBillingAgreement,
    plan?.package?.name,
    isFirstCampaign,
    paymentEventCompleted,
  ])

  return {
    handlePaymentCompletedEvents,
  }
}
